<nz-spin nzSize="large" [nzSpinning]="loading$ | async">
  <header>
    <app-lot-last-visit-date [savedLot]="(data$ | async)?.savedLot"></app-lot-last-visit-date>
    <nz-result
      *ngIf="lotNotFound$ | async"
      nzStatus="404"
      [nzTitle]="'LOT_NOT_FOUND' | translate"
    ></nz-result>
    <nz-row *ngIf="(data$ | async)?.lot as lot" nzJustify="space-between">
      <nz-col>
        <app-lot-title-full
          [lot]="lot"
          appGuideAnchor="title-block"
        ></app-lot-title-full>
      </nz-col>
      <nz-col>
        <nz-row class="tip-buttons" [nzJustify]="'space-between'">
          <app-lot-auction-tip-actions
            [auctionName]="lot?.auctionName"
          ></app-lot-auction-tip-actions>
          <button
            class="guide-btn-auto-lot"
            nz-button
            nzType="default"
            (click)="startGuide()"
          >
            {{ 'AUTO_LOT.GUIDE.BUTTON' | translate }}
            <span nz-icon nzType="question-circle" nzTheme="outline"></span>
          </button>
        </nz-row>
      </nz-col>
    </nz-row>
  </header>

  <ng-container *ngIf="data$ | async as data">
    <div *ngIf="data?.lot && data?.isSanctioned">
      <nz-result
        nzStatus="warning"
        [nzTitle]="'SANCTIONED_LOT_NOT_ALLOWED' | translate"
      ></nz-result>
    </div>
    <main *ngIf="data?.lot && !data?.isSanctioned" nz-row nzGutter="16">
      <aside nz-col nzXs="24" nzLg="10">
        <section appGuideAnchor="photo-gallery">
          <app-lot-carousel
            [savedLot]="data.savedLot"
            [lot]="data.lot"
            [lotType]="data.lotType"
            [lotSource]="data.lotSource"
            [currentUser]="data.currentUser"
            [isUsingSavedImages]="data.isUsingSavedImages"
            [inspectionData]="inspectionData$ | async"
            [(activeSlide)]="activeSlide"
            (mediaChanged)="refetch$.next()"
          ></app-lot-carousel>
        </section>

        <section>
          <app-alerts-list-auto
            [target]="AlertTargetEnum.LotPage"
            [auction]="data.lot.auctionName"
            [lotCode]="data.lot.bid"
            [company]="data.lot.company"
            [model]="data.lot.modelName"
            [frame]="data.lot.frame"
          ></app-alerts-list-auto>
        </section>
      </aside>

      <aside nz-col nzXs="24" nzLg="14">
        <section appGuideAnchor="additional-tags">
          <app-auto-lot-guide-icons *ngIf="isGuideLotIconsVisible"></app-auto-lot-guide-icons>

          <app-lot-icons
            *ngIf="!isGuideLotIconsVisible"
            [lot]="data.lot"
            [lotType]="data.lotType"
            [lotSource]="data.lotSource"
            [lotIcons]="data.icons"
            [currentUser]="data.currentUser"
          ></app-lot-icons>
        </section>

        <section>
          <app-lot-actions
            [lot]="data.lot"
            [limits]="data.limits"
            [lotType]="data.lotType"
            [lotSource]="data.lotSource"
            [savedLot]="data.savedLot"
            [bids]="data.bids"
            [isSanctioned]="data.isSanctioned"
            [aiTranslationEnabled]="(auctionData$ | async)?.aiTranslationEnabled"
            [hasTranslations]="data.hasTranslations"
            [hasManualTranslations]="data.hasManualTranslations"
            [translations]="data.translations"
            [currentUser]="data.currentUser"
            [currentUserLastBid]="data.currentUserLastBid"
            [userLotMarks]="data.currentUserLotMarks"
            [currentUserLimitsOverrideAvailable]="data.currentUserLimitsOverrideAvailable"
            [auctionViewWatchRequestData]="inspectionData$ | async"
            [auctionViewWatchRequestLoading]="inspectionLoading$ | async"
            (actionCompleted)="refetch$.next()"
            (scrollToSales)="scrollToPreviousSales()"
            (scrollToTranslations)="scrollToTranslations()"
          ></app-lot-actions>
          <app-lot-shared-includes [lotId]="data.lot?.id"></app-lot-shared-includes>
        </section>

        <nz-row [nzGutter]="[30, 10]" nzAlign="middle">
          <app-lot-vin-image [lot]="data.lot" [width]="186" [height]="50"></app-lot-vin-image>
          <nz-col [nzXs]="24" [nzMd]="14" [nzLg]="24" [nzXl]="14" [nzXXl]="12">
            <app-lot-check-production-date-input
              [lot]="data.lot"
              [lotType]="data.lotType"
              [lotSource]="data.lotSource"
              [fullVin]="data.fullVin"
              [limits]="data.limits"
              [currentUser]="data.currentUser"
              (inputFocus)="goToFirstSlide(data.isUsingSavedImages)"
            ></app-lot-check-production-date-input>
          </nz-col>
        </nz-row>

        <nz-divider></nz-divider>

        <section>
          <app-lot-translations
            id="lotTranslations"
            [translations]="data.translations"
            [lotType]="data.lotType"
            [isManager]="data.isManager"
            (actionCompleted)="refetch$.next()"
          ></app-lot-translations>
          <app-lot-note
            *ngIf="data.lot.currentUserLotNote"
            [lotNote]="data.lot.currentUserLotNote"
            (actionCompleted)="refetch$.next()"
          ></app-lot-note>
          <app-lot-statistics
            id="previousSales"
            [statisticsOpen]="statisticsOpen"
            *ngIf="data.lotType !== LotSource.Mikhail"
            [lot]="data.lot"
            appGuideAnchor="sell-stats"
          ></app-lot-statistics>
        </section>

        <section *ngIf="!data.currentUser.isOtherCountries && data.currentUser.countryIso === 'ru'">
          <app-lot-boundary-info
            [lot]="data.lot"
            [style]="'alert'"
            [excludeStatuses]="[BoundaryStatus.NotBoundary, BoundaryStatus.Early]"
          ></app-lot-boundary-info>
        </section>

        <section>
          <app-lot-main-info
            [lot]="data.lot"
            [lotType]="data.lotType"
            [lotSource]="data.lotSource"
            [bestBid]="data.bestBid"
            [hasBestBid]="data.hasBestBid"
            [currentUser]="data.currentUser"
            [hasTranslations]="data.hasTranslations"
            [currentUserLastBid]="data.currentUserLastBid"
            [currentTradingTimeShift]="(auctionData$ | async)?.currentTradingTimeShift"
            (actionComplete)="refetch$.next()"
          ></app-lot-main-info>
        </section>

        <section>
          <app-lot-check-production-date-result
            [lot]="data.lot"
          ></app-lot-check-production-date-result>
        </section>

        <section>
          <app-lot-extra-info [lot]="data.lot" [active]="true"></app-lot-extra-info>
          <app-lot-ticket-rules [lot]="data.lot"></app-lot-ticket-rules>
        </section>

        <section>
          <lib-akebono-calculator
            [lot]="data.calcData"
            appGuideAnchor="calculator"
          ></lib-akebono-calculator>
        </section>
      </aside>
    </main>
  </ng-container>
</nz-spin>
